import { useState, useMemo } from 'react';
import { Input, List, Empty, Collapse } from 'antd';
import { SearchOutlined, RightOutlined } from '@ant-design/icons';
import { helpTopics, helpCategories, HelpCategory, type HelpTopic } from '@/constants/helpContent';

interface SearchResult {
  topic: HelpTopic;
  score: number;
}

interface HelpSearchProps {
  onSelectTopic?: (topicId: string) => void;
  currentTopicId?: string;
}

const CATEGORY_ORDER: HelpCategory[] = [
  HelpCategory.GETTING_STARTED,
  HelpCategory.EQUIPMENT,
  HelpCategory.MAINTENANCE,
  HelpCategory.EMERGENCY,
  HelpCategory.USER_MANAGEMENT,
  HelpCategory.SETTINGS,
  HelpCategory.SUBSCRIPTION,
  HelpCategory.FAQ,
];

/**
 * ヘルプ検索コンポーネント
 *
 * 機能:
 * - タイトル、キーワード、カテゴリでの検索
 * - スコアリング（タイトルマッチ>キーワードマッチ>カテゴリマッチ）
 * - 上位10件のみ表示
 */
export function HelpSearch({ onSelectTopic, currentTopicId }: HelpSearchProps) {
  const [query, setQuery] = useState('');

  const categoryOrder = CATEGORY_ORDER;

  // カテゴリ別にグループ化されたトピック
  const topicsByCategory = useMemo(() => {
    return categoryOrder.map((category) => ({
      category,
      info: helpCategories[category],
      topics: helpTopics
        .filter((topic) => topic.category === category)
        .sort((a, b) => a.order - b.order),
    }));
  }, [categoryOrder]);

  // シンプルなファジー検索
  const results = useMemo(() => {
    if (!query.trim()) return [];

    const lowerQuery = query.toLowerCase();
    const scored: SearchResult[] = helpTopics
      .map((topic) => {
        let score = 0;

        // タイトルマッチ（高スコア）
        if (topic.title.toLowerCase().includes(lowerQuery)) {
          score += 10;
        }

        // キーワードマッチ（中スコア）
        topic.keywords.forEach((kw) => {
          if (kw.toLowerCase().includes(lowerQuery)) {
            score += 5;
          }
        });

        // カテゴリマッチ（低スコア）
        const categoryLabel = helpCategories[topic.category]?.label || '';
        if (categoryLabel.toLowerCase().includes(lowerQuery)) {
          score += 2;
        }

        return { topic, score };
      })
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 10); // 上位10件のみ表示

    return scored;
  }, [query]);

  return (
    <div className="help-search">
      <Input
        id="help-search-input"
        placeholder="ヘルプを検索..."
        prefix={<SearchOutlined />}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        allowClear
        size="large"
        style={{ marginBottom: query ? '16px' : 0 }}
        aria-label="ヘルプ検索"
      />

      {query ? (
        <List
          dataSource={results}
          locale={{
            emptyText: <Empty description="該当するヘルプが見つかりません" />,
          }}
          size="small"
          renderItem={({ topic }) => {
            const isActive = topic.id === currentTopicId;
            return (
            <List.Item
              onClick={() => onSelectTopic?.(topic.id)}
              style={{
                cursor: 'pointer',
                padding: '12px 8px',
              }}
              className={`help-search-result-item${isActive ? ' is-active' : ''}`}
              aria-current={isActive ? 'page' : undefined}
            >
              <List.Item.Meta
                title={<span style={{ fontSize: '16px' }}>{topic.title}</span>}
                description={helpCategories[topic.category]?.label}
              />
            </List.Item>
            );
          }}
        />
      ) : (
        <Collapse
          bordered={false}
          expandIcon={({ isActive }) => (
            <RightOutlined rotate={isActive ? 90 : 0} style={{ fontSize: 12 }} />
          )}
          style={{ background: 'transparent', marginTop: 16 }}
          items={topicsByCategory.map(({ category, info, topics }) => ({
            key: category,
            label: (
              <span style={{ fontWeight: 500 }}>
                {info.icon} {info.label}
              </span>
            ),
            children: (
              <List
                size="small"
                dataSource={topics}
                renderItem={(topic) => {
                  const isActive = topic.id === currentTopicId;
                  return (
                    <List.Item
                      onClick={() => onSelectTopic?.(topic.id)}
                      style={{
                        cursor: 'pointer',
                        padding: '8px 12px',
                        borderRadius: 4,
                      }}
                      className={`help-topic-item${isActive ? ' is-active' : ''}`}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      {topic.title}
                    </List.Item>
                  );
                }}
              />
            ),
          }))}
        />
      )}
    </div>
  );
}
